import { formatMoney } from '../core/format';
import { clickSfx } from '../platform/audio';
import type { ActionResult } from '../core/actions';
import { game } from './controller';
import { Bindings, byId, clear, h } from './dom';
import { toast } from './toasts';

/**
 * Shop panel framework. Each panel builds its DOM once per "structure key"
 * and then only refreshes bound values (prices, affordability, progress)
 * 4× per second — so buttons never get swapped out under the cursor.
 */

export type TabId = 'hens' | 'upgrades' | 'farm' | 'roosters' | 'goals' | 'stats' | 'help' | 'settings';

export interface Panel {
  id: TabId;
  label: string;
  key(): string;
  build(root: HTMLElement, b: Bindings): void;
  /** Count shown on the tab (e.g. new breeds, affordable upgrades). */
  badge?(): number;
}

const panels: Panel[] = [];
let active: TabId = 'hens';
let lastKey = '';
const bindings = new Bindings();
const tabButtons = new Map<TabId, { btn: HTMLButtonElement; badge: HTMLElement }>();
let interacting = 0;

export function registerPanels(list: Panel[]): void {
  panels.push(...list);
  const nav = byId('shop-tabs');
  for (const p of panels) {
    const badge = h('span', { class: 'badge' });
    badge.style.display = 'none';
    const btn = h('button', { class: 'shop-tab', type: 'button', onclick: () => setTab(p.id, true) }, p.label, badge);
    tabButtons.set(p.id, { btn, badge });
    nav.append(btn);
  }
  // Don't rebuild a panel while the pointer is down or a form field is focused.
  const body = byId('shop-body');
  body.addEventListener('pointerdown', () => { interacting = Date.now() + 700; }, true);
  setTab('hens');
}

export function setTab(id: TabId, userInitiated = false): void {
  active = id;
  lastKey = '';
  for (const [tid, t] of tabButtons) t.btn.classList.toggle('active', tid === id);
  refreshShop(true);
  if (userInitiated && window.matchMedia('(max-width: 900px)').matches) byId('shop').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function activeTab(): TabId {
  return active;
}

export function refreshShop(force = false): void {
  const panel = panels.find((p) => p.id === active);
  if (!panel) return;
  const key = panel.key();
  const focused = document.activeElement;
  const editing = focused instanceof HTMLInputElement || focused instanceof HTMLTextAreaElement || focused instanceof HTMLSelectElement;
  if (force || (key !== lastKey && Date.now() > interacting && !editing)) {
    lastKey = key;
    const body = byId('shop-body');
    const scroll = body.scrollTop;
    clear(body);
    bindings.reset();
    panel.build(body, bindings);
    body.scrollTop = scroll;
  } else {
    bindings.run();
  }
  for (const p of panels) {
    const n = p.badge?.() ?? 0;
    const t = tabButtons.get(p.id)!;
    t.badge.style.display = n > 0 && p.id !== active ? '' : 'none';
    t.badge.textContent = String(n);
  }
}

// ── Shared builders ────────────────────────────────────────────────────

export function section(title: string, sub?: string | HTMLElement): HTMLElement {
  return h('div', { class: 'section' }, h('h3', {}, title, sub ? h('small', {}, sub) : null));
}

/** Runs a purchase from a click: sound on success, toast on failure, save soon. */
export function purchase(fn: () => ActionResult, successText?: string): void {
  const r = fn();
  if (r.ok) {
    clickSfx('purchase');
    if (successText) toast(successText, 'good');
    game.changed();
    refreshShop(true);
  } else if (r.reason !== 'Not enough money') {
    toast(r.reason, 'warn');
  }
}

export interface BuyButtonSpec {
  cost: () => number;
  enabled?: () => boolean;
  label?: () => string;
  onBuy: () => void;
}

export function buyButton(spec: BuyButtonSpec, b: Bindings): { price: HTMLElement; btn: HTMLButtonElement } {
  const price = h('span', { class: 'price' });
  const btn = h('button', { class: 'btn small', type: 'button', onclick: () => spec.onBuy() }, 'Buy');
  b.add(() => {
    const cost = spec.cost();
    const affordable = game.state.money >= cost;
    const ok = (spec.enabled?.() ?? true) && affordable && Number.isFinite(cost);
    price.textContent = Number.isFinite(cost) ? formatMoney(cost) : '—';
    price.classList.toggle('cant', !affordable);
    btn.disabled = !ok;
    btn.textContent = spec.label?.() ?? 'Buy';
  });
  return { price, btn };
}

export interface CardSpec {
  icon?: Node | string;
  title: (Node | string)[];
  desc?: string | Node;
  meta?: string | Node;
  flavor?: string;
  side?: Node[];
  cls?: string;
  extra?: Node;
}

export function card(spec: CardSpec): HTMLElement {
  return h('div', { class: `card ${spec.cls ?? ''}` },
    spec.icon ? (typeof spec.icon === 'string' ? h('div', { class: 'icon' }, spec.icon) : spec.icon) : null,
    h('div', { class: 'body' },
      h('div', { class: 'title' }, ...spec.title),
      spec.desc ? h('div', { class: 'desc' }, spec.desc) : null,
      spec.meta ? h('div', { class: 'meta' }, spec.meta) : null,
      spec.flavor ? h('div', { class: 'flavor' }, spec.flavor) : null,
      spec.extra ?? null,
    ),
    spec.side ? h('div', { class: 'side' }, ...spec.side) : null,
  );
}

export function tag(text: string, cls = ''): HTMLElement {
  return h('span', { class: `tag ${cls}` }, text);
}
